import { useEffect, useState } from "react";
import { Inbox, LogOut, RefreshCw, Lock } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import api from "@/lib/api";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function MessageCard({ msg, index }) {
  return (
    <Reveal delay={Math.min(index * 0.04, 0.2)}>
      <article data-testid={`admin-message-${index + 1}`} className="rounded-3xl border border-ink/5 bg-white p-6 sm:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-lav-soft text-lav-deep">
              <Inbox className="h-4 w-4" />
            </span>
            <div>
              <p className="font-display text-lg font-semibold text-ink" data-testid={`admin-message-name-${index + 1}`}>
                {msg.name}
              </p>
              <a href={`mailto:${msg.email}`} className="text-sm font-semibold text-lav-deep hover:underline">
                {msg.email}
              </a>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-blush-soft px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-blush-deep">
              {msg.topic}
            </span>
            <span className="text-xs text-ink-faint">{new Date(msg.created_at).toLocaleString()}</span>
          </div>
        </div>
        <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-ink-soft">{msg.message}</p>
      </article>
    </Reveal>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [messages, setMessages] = useState([]);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/contact/messages");
      setMessages(data);
      setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) setAuthed(false);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/login", { password });
      toast.success("Welcome back.");
      setPassword("");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/admin/logout");
    } catch {}
    setAuthed(false);
    setMessages([]);
  };

  if (authed === null) {
    return (
      <main data-testid="page-admin" className="pt-40 pb-24 text-center">
        <p className="text-ink-soft">Checking…</p>
      </main>
    );
  }

  if (!authed) {
    return (
      <main data-testid="page-admin" className="pt-40 pb-24">
        <div className="mx-auto max-w-md px-5">
          <Reveal>
            <form
              onSubmit={login}
              data-testid="admin-login-form"
              className="rounded-3xl border border-ink/5 bg-white p-8 shadow-xl shadow-lav-soft/50"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-lav-soft text-lav-deep">
                <Lock className="h-5 w-5" />
              </span>
              <h1 className="mt-5 font-display text-2xl font-semibold text-ink" data-testid="admin-login-headline">
                Private area
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                Enter the admin password to read contact messages.
              </p>
              <input
                type="password"
                data-testid="admin-login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                className="mt-5 w-full rounded-2xl border border-ink/10 bg-cream px-5 py-4 text-sm text-ink outline-none transition-colors placeholder:text-ink-faint focus:border-lav focus:ring-2 focus:ring-lav/25"
                required
              />
              {error && (
                <p className="mt-3 text-sm font-semibold text-blush-deep" data-testid="admin-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-submit"
                disabled={busy}
                className="mt-5 w-full rounded-full bg-ink px-8 py-4 font-bold text-cream transition-all duration-300 hover:bg-lav-deep disabled:opacity-60"
              >
                {busy ? "Checking…" : "Open the inbox"}
              </button>
            </form>
          </Reveal>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="page-admin" className="pt-36 pb-24 sm:pt-44">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Eyebrow>Admin inbox</Eyebrow>
            <h1
              className="mt-4 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl"
              data-testid="admin-inbox-headline"
            >
              Every message, all in one place.
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-3 text-sm font-bold text-ink transition-colors hover:border-lav hover:text-lav-deep"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-bold text-cream transition-colors hover:bg-lav-deep"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
        <div className="mt-10 space-y-5">
          {messages.length === 0 && (
            <p className="rounded-3xl bg-mint-soft p-8 text-center text-ink-soft" data-testid="admin-empty">
              No messages yet. When someone writes, it lands here—and a copy goes to your email.
            </p>
          )}
          {messages.map((m, i) => (
            <MessageCard key={m.id || i} msg={m} index={i} />
          ))}
        </div>
      </div>
    </main>
  );
}
